/**
 * Displays an operator-facing country label as "<English full name>（<中文名>）".
 *
 * Operators see a mix of ISO codes ("KR"), English names ("United States") and Chinese names
 * ("日本") depending on which evidence produced the value, and a two-letter code alone is not
 * readable at a glance. The label keeps one readable English name plus the Chinese name; the
 * stored value is never rewritten, so downstream rules that compare country codes keep working.
 */
const ISO_ALPHA2_CODES =
  "AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW";

/** Names that Intl does not produce (common aliases, historic and short forms). */
const COUNTRY_NAME_ALIASES: Readonly<Record<string, string>> = {
  america: "US",
  britain: "GB",
  burma: "MM",
  "cape verde": "CV",
  czechia: "CZ",
  "czech republic": "CZ",
  deutschland: "DE",
  england: "GB",
  "great britain": "GB",
  holland: "NL",
  "hong kong": "HK",
  "hong kong sar": "HK",
  "ivory coast": "CI",
  korea: "KR",
  "korea, republic of": "KR",
  "korea, south": "KR",
  macao: "MO",
  macau: "MO",
  "mainland china": "CN",
  macedonia: "MK",
  "north korea": "KP",
  "p.r. china": "CN",
  prc: "CN",
  "republic of korea": "KR",
  "russian federation": "RU",
  swaziland: "SZ",
  "the netherlands": "NL",
  "the philippines": "PH",
  turkey: "TR",
  turkiye: "TR",
  türkiye: "TR",
  "u.k.": "GB",
  "u.s.": "US",
  "u.s.a.": "US",
  uae: "AE",
  uk: "GB",
  usa: "US",
  "viet nam": "VN",
};

interface CountryRegistry {
  readonly resolve: Map<string, string>;
  readonly english: Intl.DisplayNames;
  readonly chinese: Intl.DisplayNames;
}

let registry: CountryRegistry | null = null;

function regionName(locale: "en" | "zh-CN", code: string): string | null {
  try {
    const name = new Intl.DisplayNames([locale], { type: "region" }).of(code);
    return name && name !== code ? name : null;
  } catch {
    return null;
  }
}

function countryRegistry(): CountryRegistry {
  if (registry) return registry;
  const resolved = new Map<string, string>();
  for (const code of ISO_ALPHA2_CODES.split(" ")) {
    resolved.set(code.toLowerCase(), code);
    const english = regionName("en", code);
    const chinese = regionName("zh-CN", code);
    if (english) resolved.set(english.toLowerCase(), code);
    if (chinese) resolved.set(chinese, code);
  }
  for (const [alias, code] of Object.entries(COUNTRY_NAME_ALIASES)) {
    resolved.set(alias, code);
  }
  registry = {
    resolve: resolved,
    english: new Intl.DisplayNames(["en"], { type: "region" }),
    chinese: new Intl.DisplayNames(["zh-CN"], { type: "region" }),
  };
  return registry;
}

/** Resolves a stored country value to an ISO 3166-1 alpha-2 code, or null when unrecognizable. */
export function resolveCountryCode(value: string | null | undefined): string | null {
  const raw = (value ?? "").trim();
  if (!raw) return null;
  if (/^[A-Za-z]{2}$/.test(raw)) {
    const code = raw.toUpperCase();
    return countryRegistry().resolve.has(code.toLowerCase()) ? code : null;
  }
  return countryRegistry().resolve.get(raw.toLocaleLowerCase("en-US")) ?? null;
}

/** Renders "<English full name>（<中文>）"; unrecognizable values are returned unchanged. */
export function formatCountryDisplay(value: string | null | undefined): string {
  const raw = (value ?? "").trim();
  if (!raw) return "";
  if (/[（(]/.test(raw)) return raw;
  const code = resolveCountryCode(raw);
  if (!code) return raw;
  const { english, chinese } = countryRegistry();
  const englishName = english.of(code) ?? code;
  const chineseName = chinese.of(code) ?? null;
  return chineseName && chineseName !== englishName
    ? `${englishName}（${chineseName}）`
    : englishName;
}
